import { Container, FillGradient, Text } from 'pixi.js';
import {
  AnimationState,
  AnimationStateData,
  Skeleton,
} from '@esotericsoftware/spine-core';
import { Physics, Spine, AtlasAttachmentLoader, SkeletonJson } from '@esotericsoftware/spine-pixi-v8';
import { gameConfig } from '../config/gameConfig.js';
import { getCelebrationAnticipationSec } from '../effects/cabinetJuice.js';
import { formatCurrencyCelebrationAmount } from '../lib/formatCurrency.js';
import { sound } from '../utils/soundManager.js';

/** @typedef {'big' | 'huge' | 'mega' | 'epic'} WinTier */

/**
 * Minimum win÷stake to run the celebration overlay (see {@link gameConfig.winCelebrationTestTiers}).
 * @returns {number}
 */
export function winCelebrationMinMultiple() {
  if (gameConfig.winCelebrationTestTiers) return 1;
  const t = gameConfig.winCelebrationTierMultiples;
  return Number.isFinite(t?.big) ? t.big : 20;
}

/**
 * Hold + collect clips for each tier's final popup art.
 * @type {Record<WinTier, { idle: string, outro: string }>}
 */
const WIN_SPINE_CLIPS = {
  big: { idle: 'big_win_idle', outro: 'big_win_end' },
  huge: { idle: 'huge_win_idle', outro: 'huge_win_end' },
  mega: { idle: 'mega_win_idle', outro: 'mega_win_end' },
  epic: { idle: 'epic_win_idle', outro: 'epic_win_end' },
};

/**
 * One-shot intro chain before the tier idle (collect outro is separate).
 * Higher tiers chain through each lower tier's start → end → next start.
 * @type {Record<WinTier, string[]>}
 */
const TIER_BUILDUP_CHAINS = {
  big: ['big_win_start'],
  huge: ['big_win_start', 'big_win_end', 'huge_win_start'],
  mega: ['big_win_start', 'big_win_end', 'huge_win_start', 'huge_win_end', 'mega_win_start'],
  epic: [
    'big_win_start',
    'big_win_end',
    'huge_win_start',
    'huge_win_end',
    'mega_win_start',
    'mega_win_end',
    'epic_win_start',
  ],
};

/**
 * @param {WinTier} tier
 * @returns {{ idle: string, outro: string }}
 */
function tierWinSpineClips(tier) {
  return WIN_SPINE_CLIPS[tier] ?? WIN_SPINE_CLIPS.big;
}

/**
 * @param {WinTier} tier
 * @returns {string[]}
 */
function tierBuildupChain(tier) {
  return TIER_BUILDUP_CHAINS[tier] ?? TIER_BUILDUP_CHAINS.big;
}

/**
 * @param {string} clipName
 * @param {WinTier} tier
 * @returns {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin' | null}
 */
function soundForBuildupClip(clipName, _tier) {
  if (clipName === 'big_win_start') return 'bigWin';
  if (clipName === 'huge_win_start') return 'hugeWin';
  if (clipName === 'mega_win_start') return 'megaWin';
  if (clipName === 'epic_win_start') return 'epicWin';
  return null;
}

/**
 * @param {string} clipName
 * @param {WinTier} tier
 * @returns {boolean}
 */
function isFinalTierSoundClip(clipName, tier) {
  const chain = tierBuildupChain(tier);
  for (let i = chain.length - 1; i >= 0; i--) {
    if (soundForBuildupClip(chain[i], tier)) return chain[i] === clipName;
  }
  return false;
}

/**
 * Popup SFX for the highest tier reached in this celebration (big / huge / mega / epic).
 * The last popup in the chain always uses {@link EPIC_WIN_SOUND} so the sting finishes correctly.
 * @param {WinTier} tier
 * @returns {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin' | null}
 */
const EPIC_WIN_SOUND = 'epicWin';

function soundForFinalTier(tier) {
  const chain = tierBuildupChain(tier);
  for (let i = chain.length - 1; i >= 0; i--) {
    if (soundForBuildupClip(chain[i], tier)) return EPIC_WIN_SOUND;
  }
  return null;
}

/**
 * SFX for a buildup intro clip — tier-matched until the final popup, which always uses epic.
 * @param {string} clipName
 * @param {WinTier} tier
 * @returns {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin' | null}
 */
function soundForBuildupClipPlayback(clipName, tier) {
  if (!soundForBuildupClip(clipName, tier)) return null;
  if (isFinalTierSoundClip(clipName, tier)) return EPIC_WIN_SOUND;
  return soundForBuildupClip(clipName, tier);
}

/**
 * Idle loop played after a short buildup intro clip (not a replay of the intro).
 * @param {string} clipName
 * @returns {string | null}
 */
function holdIdleAfterBuildupClip(clipName) {
  if (clipName === 'big_win_start') return 'big_win_idle';
  if (clipName === 'huge_win_start') return 'huge_win_idle';
  if (clipName === 'mega_win_start') return 'mega_win_idle';
  if (clipName === 'epic_win_start') return 'epic_win_idle';
  return null;
}

/** Spine intro clip → celebration tier (amount milestones align with these popups). */
const CLIP_TIER_START = {
  big_win_start: 'big',
  huge_win_start: 'huge',
  mega_win_start: 'mega',
  epic_win_start: 'epic',
};

/** @type {WinTier[]} */
const TIER_ORDER = ['big', 'huge', 'mega', 'epic'];

/**
 * Amount target when a non-final popup ends — the **next** tier's entry multiple
 * (e.g. Big Win popup counts up to 30×, then Huge Win takes over at 30×).
 * @param {WinTier} tier
 * @param {WinTier} finalTier
 * @param {{ big: number, huge: number, mega: number, epic: number }} thresholds
 * @param {number} stakeAmt
 * @param {number} total
 */
function celebrationStepEndAmount(tier, finalTier, thresholds, stakeAmt, total) {
  if (tier === finalTier) return total;
  const idx = TIER_ORDER.indexOf(tier);
  const nextTier = TIER_ORDER[idx + 1];
  if (!nextTier) return total;
  return Math.min(total, stakeAmt * thresholds[nextTier]);
}

/**
 * Win÷stake gate for each celebration popup tier.
 * @returns {{ big: number, huge: number, mega: number, epic: number }}
 */
export function getCelebrationTierThresholdMultiples() {
  if (gameConfig.winCelebrationTestTiers) {
    return { big: 1, huge: 2, mega: 5, epic: 10 };
  }
  const t = gameConfig.winCelebrationTierMultiples ?? {};
  return {
    big: Number.isFinite(t.big) ? t.big : 20,
    huge: Number.isFinite(t.huge) ? t.huge : 30,
    mega: Number.isFinite(t.mega) ? t.mega : 40,
    epic: Number.isFinite(t.epic) ? t.epic : 50,
  };
}

/**
 * @typedef {{ startSec: number, durationSec: number, from: number, to: number }} CelebrationAmountStep
 * @typedef {{ steps: CelebrationAmountStep[], totalSec: number }} CelebrationAmountTimeline
 */

/**
 * Quick Spin shortens the celebration the same way the live 10-line game does.
 * @param {boolean} [turbo]
 * @returns {{ minSec: number, timeScale: number }}
 */
function celebrationClipPlayback(turbo = false) {
  const minSec = Math.max(
    0.05,
    Number(
      turbo
        ? gameConfig.winAnimationMinDisplaySecTurbo ?? gameConfig.winAnimationMinDisplaySec
        : gameConfig.winAnimationMinDisplaySec,
    ) || (turbo ? 0.9 : 3),
  );
  const timeScale = turbo
    ? Math.max(1, Number(gameConfig.turboCelebrationTimeScale) || 1.4)
    : 1;
  return { minSec, timeScale };
}

/**
 * Amount count-up steps synced to each `*_win_start` popup (holds during `*_win_end` transitions).
 * Each non-final popup counts to the **next** tier's entry multiple (Big → 30×, Huge → 40×, Mega → 50×).
 * The final popup counts to the actual win.
 * @param {Record<string, unknown> | null | undefined} spineAssets
 * @param {number} multiple win ÷ stake
 * @param {number} stake
 * @param {number} totalWin
 * @param {boolean} [turbo]
 * @returns {CelebrationAmountTimeline | null}
 */
export function computeCelebrationAmountTimeline(spineAssets, multiple, stake, totalWin, turbo = false) {
  const finalTier = winMultipleToTier(multiple);
  if (!finalTier) return null;

  const data = getCelebrationSkeletonData(spineAssets);
  if (!data) return null;

  const chain = tierBuildupChain(finalTier);
  const { minSec, timeScale } = celebrationClipPlayback(turbo);
  const thresholds = getCelebrationTierThresholdMultiples();
  const stakeAmt = Math.max(0, Number(stake) || 0);
  const total = Math.max(0, Number(totalWin) || 0);

  /** @type {CelebrationAmountStep[]} */
  const steps = [];
  let time = 0;
  let prevAmount = 0;

  for (const clipName of chain) {
    const dur = clipDurationSeconds(data, clipName) / timeScale;
    const tier = CLIP_TIER_START[clipName];

    if (tier) {
      const stepDur = Math.max(minSec, dur);
      const toAmount = celebrationStepEndAmount(tier, finalTier, thresholds, stakeAmt, total);

      steps.push({
        startSec: time,
        durationSec: stepDur,
        from: prevAmount,
        to: toAmount,
      });
      prevAmount = toAmount;
      time += stepDur;
    } else {
      time += dur;
    }
  }

  if (!steps.length) return null;
  return { steps, totalSec: time };
}

/**
 * @param {CelebrationAmountTimeline} timeline
 * @param {number} elapsedSec
 * @returns {number}
 */
export function celebrationAmountAtElapsed(timeline, elapsedSec) {
  const t = Math.max(0, Number(elapsedSec) || 0);
  let lastAmount = 0;
  for (const step of timeline.steps) {
    if (t < step.startSec) return lastAmount;
    if (t < step.startSec + step.durationSec) {
      const local = (t - step.startSec) / step.durationSec;
      return step.from + (step.to - step.from) * Math.min(1, local);
    }
    lastAmount = step.to;
  }
  return lastAmount;
}

/**
 * Payout multiple (win ÷ stake) → celebration tier.
 * Production ({@link gameConfig.winCelebrationTierMultiples}): 20–30 big, 30–40 huge, 40–50 mega, 50+ epic.
 * Test ({@link gameConfig.winCelebrationTestTiers}): 1–2 big, 2–5 huge, 5–10 mega, 10+ epic.
 * @param {number} multiple
 * @returns {WinTier | null}
 */
export function winMultipleToTier(multiple) {
  const m = Number(multiple);
  if (!Number.isFinite(m)) return null;

  if (gameConfig.winCelebrationTestTiers) {
    if (m < 1) return null;
    if (m >= 10) return 'epic';
    if (m >= 5) return 'mega';
    if (m >= 2) return 'huge';
    return 'big';
  }

  const t = gameConfig.winCelebrationTierMultiples ?? {};
  const big = Number.isFinite(t.big) ? t.big : 20;
  const huge = Number.isFinite(t.huge) ? t.huge : 30;
  const mega = Number.isFinite(t.mega) ? t.mega : 40;
  const epic = Number.isFinite(t.epic) ? t.epic : 50;
  if (m < big) return null;
  if (m >= epic) return 'epic';
  if (m >= mega) return 'mega';
  if (m >= huge) return 'huge';
  return 'big';
}

/** Cached skeleton data for reading clip durations (same atlas/json as the live Spine). */
let _celebrationSkeletonData = null;

/**
 * @param {Record<string, unknown> | null | undefined} spineAssets
 * @returns {import('@esotericsoftware/spine-core').SkeletonData | null}
 */
function getCelebrationSkeletonData(spineAssets) {
  if (_celebrationSkeletonData) return _celebrationSkeletonData;
  const row = spineAssets?.winCelebration;
  if (!row?.skeletonData || !row?.textureAtlas) return null;
  try {
    const attachmentLoader = new AtlasAttachmentLoader(row.textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);
    _celebrationSkeletonData = skeletonJson.readSkeletonData(row.skeletonData);
    return _celebrationSkeletonData;
  } catch {
    return null;
  }
}

/**
 * @param {import('@esotericsoftware/spine-core').SkeletonData} data
 * @param {string} clipName
 */
function clipDurationSeconds(data, clipName) {
  const anim = data.findAnimation(clipName);
  return anim?.duration ?? 0;
}

/**
 * Total Spine sequence duration (seconds) for the celebration tier / turbo mode.
 * Used to sync last-win count-up with the overlay animation.
 * @param {Record<string, unknown> | null | undefined} spineAssets
 * @param {number} multiple win ÷ stake
 * @param {boolean} turbo
 * @returns {number}
 */
export function getCelebrationSequenceDurationSec(spineAssets, multiple, turbo = false) {
  const data = getCelebrationSkeletonData(spineAssets);
  const tier = winMultipleToTier(multiple);
  const { minSec, timeScale } = celebrationClipPlayback(turbo);
  if (!data || !tier) return minSec;

  const chain = tierBuildupChain(tier);
  let total = 0;
  for (const name of chain) {
    const d = clipDurationSeconds(data, name) / timeScale;
    if (holdIdleAfterBuildupClip(name)) {
      total += Math.max(minSec, d);
    } else {
      total += d;
    }
  }
  const anticipation = turbo ? 0 : getCelebrationAnticipationSec();
  return Math.max(minSec, total || minSec) + anticipation;
}

/** Max fraction of the logical viewport used as the fit box (leave safe margins). */
const FIT_VIEWPORT_W = 0.86;
const FIT_VIEWPORT_H = 0.76;
/** Extra inset applied after fitting to the envelope (prevents edge clipping). */
const FIT_ENVELOPE_PAD = 0.96;
/** Global shrink/grow for the popup + amount (1 = fill fit box; lower = smaller). */
const CELEBRATION_SIZE_MUL = 0.72;
/** Vertical offset for the overlay anchor (fraction of logical screen height). Negative = higher on screen. */
const ROOT_OFFSET_Y_FRAC = -0.09;

/** Drop shadow under the amount (warm dark brown — casino legibility on gold art). */
const AMOUNT_DROP_SHADOW_COLOR = '#1A0A00';
/** Shadow Y offset at 100px design size; scaled in {@link WinCelebrationOverlay#_styleAmountText}. */
const AMOUNT_BOX_SHADOW_OFFSET_Y = 4;

/** Stroke width at 100px design size; scaled in {@link WinCelebrationOverlay#_styleAmountText}. */
const AMOUNT_BORDER_STROKE_PX = 4;
/** Bone that owns the popup title band — sampled once at fit (not per-frame). */
const AMOUNT_ANCHOR_BONE = 'bone6';
/** Setup height of the `big_win` attachment (skeleton units); font size + Y nudge. */
const BIG_WIN_ATTACH_H = 597;
/** Skeleton-space nudge below {@link AMOUNT_ANCHOR_BONE} (fraction of attach height + extra px). */
const AMOUNT_Y_FRAC_OF_BIG_WIN = 0.48;
const AMOUNT_EXTRA_OFFSET_Y = 90;
/** Fallback amount Y in {@link WinCelebrationOverlay#_celebrationContent} space. */
const AMOUNT_FALLBACK_Y = 40;
/** Win amount font at 1080p logical height (scales with viewport; tweak this to resize). */
const AMOUNT_FONT_SIZE_REF = 128;
const AMOUNT_FONT_SIZE_MAX = 200;
const AMOUNT_FONT_SIZE_MIN = 56;

/**
 * Spine win celebration: `public/assets/spines/win_animations/`.
 * Root is placed slightly above logical viewport center ({@link setScreenCenterLayout}); skeleton is centered on that point.
 * Build-up chain (big → huge → mega → epic) → loop final `{tier}_idle` → collect `{tier}_end`.
 */
export class WinCelebrationOverlay {
  /**
   * @param {Record<string, unknown>} spineAssets
   */
  constructor(spineAssets) {
    this._assets = spineAssets;
    /** Scale box for the popup (logical px, updated in {@link setScreenCenterLayout}). */
    this._layout = { maxW: 900, maxH: 700, screenW: 1922, screenH: 1080 };
    /** @type {{ minX: number, minY: number, maxX: number, maxY: number } | null} */
    this._animationEnvelope = null;
    this._gen = 0;
    /** @type {WinTier | null} */
    this._activeTier = null;
    this._activeTurbo = false;
    this._endListener = null;
    /** In-flight `endXxxWin` Promise; reused so re-entry never restarts or aborts the end clip. @type {Promise<void> | null} */
    this._skipPromise = null;
    /** @type {number | null} */
    this._amountRollupRaf = null;
    /** Final formatted amount width — keeps count-up from shifting horizontally. */
    this._amountRollupSlotWidth = 0;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._buildupStepTimer = null;
    this._buildupComplete = false;
    /** Pre-popup cabinet shake — collect tap should jump to final tier hold, not dismiss. */
    this._inAnticipation = false;
    this._activeWinAmount = 0;
    this._activeStake = 0;
    this._activeMultiple = 0;
    /** @type {(() => void) | null} */
    this._onBuildupComplete = null;
    /** Fixed amount Y in {@link #_celebrationContent} space (set in {@link #_fitSpine}; not bone-tracked). */
    this._amountFixedY = 0;
    this._currencyCode = 'FUN';
    this._currencyPrecision = 2;
    /** @type {string | undefined} */
    this._currencySymbol = undefined;
    /**
     * Uniform scale for Spine + win amount only (child of {@link #root}).
     * Lets GameApp shrink the celebration on small portrait phones without touching aspect compensation on {@link #root}.
     */
    this._celebrationContentScale = 1;
    /** Portrait canvas `scaleY/scaleX`; applied on {@link #_celebrationContent} only (root stays 1,1). */
    this._canvasStretchMulX = 1;
    /** Extra root Y in logical px ({@link #setCelebrationRootExtraOffsetYPx}); positive = lower on screen. */
    this._extraRootOffsetY = 0;
    /** @type {import('../effects/cabinetJuice.js').CabinetJuice | null} */
    this._juiceHost = null;

    this.root = new Container();
    this.root.visible = false;
    this.root.eventMode = 'none';
    this.root.pivot.set(0, 0);

    this._celebrationContent = new Container();
    this._celebrationContent.eventMode = 'none';
    this.root.addChild(this._celebrationContent);

    this._winFillGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 1 },
      end: { x: 0, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: '#FF8C00' },
        { offset: 0.38, color: '#FFD700' },
        { offset: 0.72, color: '#FFEE58' },
        { offset: 1, color: '#FFFDE7' },
      ],
    });

    this._amountBorderGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 1 },
      end: { x: 0, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: '#0D0500' },
        { offset: 0.55, color: '#2A1200' },
        { offset: 1, color: '#4A2808' },
      ],
    });

    this._amountLayer = new Container();
    this._amountLayer.eventMode = 'none';
    this._amountLayer.roundPixels = true;
    const mainStyle = {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: '700',
      fontSize: 100,
      lineHeight: 122,
      fill: this._winFillGradient,
      stroke: {
        width: AMOUNT_BORDER_STROKE_PX,
        fill: this._amountBorderGradient,
      },
      /** Hard offset shadow — dark brown for contrast on pyramid art */
      dropShadow: {
        alpha: 0.92,
        angle: Math.PI / 2,
        blur: 0,
        color: AMOUNT_DROP_SHADOW_COLOR,
        distance: AMOUNT_BOX_SHADOW_OFFSET_Y,
      },
    };
    this._amountMain = new Text({ text: '', style: mainStyle });
    this._amountMain.roundPixels = true;
    this._amountMain.anchor.set(0.5);
    this._amountLayer.addChild(this._amountMain);

    this._spine = this._createSpine();
    if (this._spine) {
      this._fitSpine();
      this._celebrationContent.addChild(this._spine);
    }
    /** Sibling of Spine — fixed position; does not follow modal scale/bone animation. */
    this._celebrationContent.addChild(this._amountLayer);
  }

  /**
   * @param {{ currencyCode?: string, currencyPrecision?: number, currencySymbol?: string }} [opts]
   */
  setCurrency(opts = {}) {
    if (opts.currencyCode != null && String(opts.currencyCode).trim() !== '') {
      this._currencyCode = String(opts.currencyCode).trim().toUpperCase();
    }
    if (Number.isFinite(Number(opts.currencyPrecision))) {
      this._currencyPrecision = Number(opts.currencyPrecision);
    }
    const sym = opts.currencySymbol;
    this._currencySymbol =
      sym == null || String(sym).trim() === '' ? undefined : String(sym).trim();
  }

  _formatCelebrationWinAmount(amount) {
    if (gameConfig.winCelebrationFixedTestAmount) {
      return '$123.456.07';
    }
    return formatCurrencyCelebrationAmount(
      amount,
      this._currencyCode,
      this._currencyPrecision,
      this._currencySymbol,
    );
  }

  /**
   * Shrink or grow the Spine + amount block uniformly (default 1). Used on narrow portrait where reels are scaled down but this overlay is not.
   * @param {number} mul clamped to ~0.35–1.38
   */
  setCelebrationContentScale(mul) {
    const m = Math.max(0.35, Math.min(1.38, Number(mul) || 1));
    this._celebrationContentScale = m;
    this._applyCelebrationContentScale();
  }

  /** @param {number} mulX portrait canvas `scaleY/scaleX`, else `1` — call before {@link #setScreenCenterLayout}. */
  setCanvasStretchMulX(mulX) {
    const x = Number(mulX);
    this._canvasStretchMulX = Number.isFinite(x) && x > 0 ? Math.max(0.25, Math.min(4, x)) : 1;
  }

  _applyCelebrationContentScale() {
    const m = this._celebrationContentScale;
    const sx = m * this._canvasStretchMulX;
    this._celebrationContent.scale.set(sx, m);
    this._styleAmountText();
    this._syncAmountPosition();
  }

  /**
   * Place the overlay at the logical viewport center (same space as {@link SlotMachine#layoutScreen})
   * and size the animation to fit the viewport.
   * @param {number} screenW
   * @param {number} screenH
   */
  setScreenCenterLayout(screenW, screenH) {
    const sw = Math.max(1, Number(screenW) || 1);
    const sh = Math.max(1, Number(screenH) || 1);
    this._layout.screenW = sw;
    this._layout.screenH = sh;
    this._layout.maxW = sw * FIT_VIEWPORT_W;
    this._layout.maxH = sh * FIT_VIEWPORT_H;
    this.root.scale.set(1, 1);
    this._celebrationContent.position.set(0, 0);
    this.root.position.set(sw / 2, sh / 2 + sh * ROOT_OFFSET_Y_FRAC + this._extraRootOffsetY);
    this._fitSpine();
    this._styleAmountText();
    this._syncAmountPosition();
  }

  /**
   * Nudges the celebration anchor vertically (e.g. tablet). Cleared when not used; call with 0 to reset.
   * @param {number} px logical screen px; positive moves the animation lower.
   */
  setCelebrationRootExtraOffsetYPx(px) {
    this._extraRootOffsetY = Number(px) || 0;
    const sw = this._layout.screenW ?? 0;
    const sh = this._layout.screenH ?? 0;
    if (sw >= 1 && sh >= 1) {
      this.root.position.set(sw / 2, sh / 2 + sh * ROOT_OFFSET_Y_FRAC + this._extraRootOffsetY);
    }
  }

  /** Font + stroke for the currency label (does not move the layer). */
  _styleAmountText() {
    if (!this._amountMain) return;
    const sh = Math.max(1, this._layout.screenH ?? 1080);
    const fs = Math.round(
      Math.min(
        AMOUNT_FONT_SIZE_MAX,
        Math.max(AMOUNT_FONT_SIZE_MIN, AMOUNT_FONT_SIZE_REF * (sh / 1080)),
      ),
    );
    const lh = Math.round(fs * 1.22);
    this._amountMain.style.fontSize = fs;
    this._amountMain.style.lineHeight = lh;
    const borderW = Math.max(1, Math.round((AMOUNT_BORDER_STROKE_PX * fs) / 100));
    const shadowDist = Math.max(1, Math.round((AMOUNT_BOX_SHADOW_OFFSET_Y * fs) / 100));
    this._amountMain.style.stroke = {
      width: borderW,
      fill: this._amountBorderGradient,
    };
    this._amountMain.style.dropShadow = {
      alpha: 0.92,
      angle: Math.PI / 2,
      blur: 0,
      color: AMOUNT_DROP_SHADOW_COLOR,
      distance: shadowDist,
    };
  }

  /**
   * Stable amount anchor from hold-pose bone (sampled once in {@link #_fitSpine}; not bone-tracked).
   */
  _layoutAmountAnchor() {
    if (!this._spine?.skeleton || !this._amountLayer) return;

    this._spine.skeleton.updateWorldTransform(Physics.update);
    const bone = this._spine.skeleton.findBone(AMOUNT_ANCHOR_BONE);
    const sy = this._spine.scale.y;

    if (bone) {
      const boneScaleY = Math.hypot(bone.c, bone.d) || 1;
      const yNudge =
        (BIG_WIN_ATTACH_H * AMOUNT_Y_FRAC_OF_BIG_WIN + AMOUNT_EXTRA_OFFSET_Y) * boneScaleY;
      this._amountFixedY = this._spine.y + (bone.worldY + yNudge) * sy;
    } else {
      this._amountFixedY = AMOUNT_FALLBACK_Y;
    }

    this._amountLayer.position.set(0, Math.round(this._amountFixedY));
  }

  /** Re-applies the fixed anchor (does not re-read live animation bounds). */
  _syncAmountPosition() {
    if (!this._amountLayer) return;
    this._amountLayer.position.set(0, Math.round(this._amountFixedY));
  }

  _layoutAmountText() {
    this._styleAmountText();
    this._syncAmountPosition();
  }

  /** Apply queued Spine tracks to the skeleton immediately (frame 0). */
  _applySpineStateNow() {
    const spine = this._spine;
    const state = spine?.state;
    if (!spine?.skeleton || !state) return;
    state.update(0);
    state.apply(spine.skeleton);
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  _createSpine() {
    const res = this._assets?.winCelebration;
    if (!res?.skeletonData || !res?.textureAtlas) return null;
    try {
      const attachmentLoader = new AtlasAttachmentLoader(res.textureAtlas);
      const skeletonJson = new SkeletonJson(attachmentLoader);
      const skeleton = skeletonJson.readSkeletonData(res.skeletonData);
      const spine = new Spine(skeleton);
      spine.skeleton.setToSetupPose();
      spine.skeleton.updateWorldTransform(Physics.update);
      return spine;
    } catch (e) {
      console.warn('WinCelebrationOverlay: failed to create spine', e);
      return null;
    }
  }

  /**
   * Union of skeleton attachment bounds across every animation (sampled over time).
   * Setup-pose DisplayObject bounds are often far too small, which over-scales the popup.
   * @returns {{ minX: number, minY: number, maxX: number, maxY: number } | null}
   */
  _computeAnimationEnvelope() {
    const data = this._spine?.skeleton?.data;
    if (!data?.animations?.length) return null;

    const stateData = new AnimationStateData(data);
    const asm = new AnimationState(stateData);
    const sk = new Skeleton(data);

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    const merge = (r) => {
      if (!r || r.width === Number.NEGATIVE_INFINITY || !Number.isFinite(r.x)) return;
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    };

    sk.setToSetupPose();
    sk.updateWorldTransform(Physics.update);
    merge(sk.getBoundsRect());

    for (let ai = 0; ai < data.animations.length; ai++) {
      const anim = data.animations[ai];
      if (!anim?.duration || anim.duration <= 0) continue;

      sk.setToSetupPose();
      asm.clearTracks();
      asm.setAnimation(0, anim.name, false);

      const duration = anim.duration;
      const timeStep = Math.max(duration / 24, 1 / 30);
      const steps = Math.max(Math.ceil(duration / timeStep), 1);
      for (let i = 0; i <= steps; i++) {
        const delta = i > 0 ? timeStep : 0;
        asm.update(delta);
        asm.apply(sk);
        sk.update(delta);
        sk.updateWorldTransform(Physics.update);
        merge(sk.getBoundsRect());
      }
    }

    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
    return { minX, minY, maxX, maxY };
  }

  /**
   * Park skeleton at the post-intro hold pose so fit/center matches what the player sees.
   */
  _parkAtHoldPose(tier = this._activeTier ?? 'big') {
    const spine = this._spine;
    const state = spine?.state;
    const skeletonData = spine?.skeleton?.data;
    if (!spine?.skeleton) return;

    if (!state || !skeletonData) {
      spine.skeleton.setToSetupPose();
      spine.skeleton.updateWorldTransform(Physics.update);
      return;
    }

    const clips = tierWinSpineClips(tier);
    state.clearTracks();
    const idle = skeletonData.findAnimation(clips.idle);
    const chain = tierBuildupChain(tier);
    const lastBuildup = chain.length ? skeletonData.findAnimation(chain[chain.length - 1]) : null;
    if (idle) {
      const entry = state.setAnimation(0, clips.idle, true);
      if (entry) entry.mixDuration = 0;
    } else if (lastBuildup) {
      const entry = state.setAnimation(0, chain[chain.length - 1], false);
      if (entry) {
        entry.mixDuration = 0;
        entry.trackTime = entry.animationEnd;
      }
    } else {
      spine.skeleton.setToSetupPose();
    }
    state.update(0);
    state.apply(spine.skeleton);
    spine.skeleton.updateWorldTransform(Physics.update);
  }

  _fitSpine() {
    const spine = this._spine;
    if (!spine?.skeleton) return;
    const maxW = Math.max(120, this._layout.maxW);
    const maxH = Math.max(120, this._layout.maxH);
    const data = spine.skeleton.data;

    spine.scale.set(1);
    spine.x = 0;
    spine.y = 0;
    this._parkAtHoldPose();

    const dataW = typeof data.width === 'number' && data.width > 0 ? data.width : 0;
    const dataH = typeof data.height === 'number' && data.height > 0 ? data.height : 0;
    const holdBounds = spine.getBounds(false);

    let bw;
    let bh;
    if (dataW > 0 && dataH > 0) {
      bw = dataW;
      bh = dataH;
    } else if (!holdBounds.isEmpty()) {
      bw = Math.max(1, holdBounds.maxX - holdBounds.minX);
      bh = Math.max(1, holdBounds.maxY - holdBounds.minY);
    } else {
      if (!this._animationEnvelope) {
        this._animationEnvelope = this._computeAnimationEnvelope();
      }
      const env = this._animationEnvelope;
      if (env) {
        bw = Math.max(1, env.maxX - env.minX);
        bh = Math.max(1, env.maxY - env.minY);
      } else {
        const b = spine.getBounds();
        bw = Math.max(1, b.width);
        bh = Math.max(1, b.height);
      }
    }

    const sRaw = Math.min(maxW / bw, maxH / bh) * FIT_ENVELOPE_PAD * CELEBRATION_SIZE_MUL;
    const s = Number.isFinite(sRaw) && sRaw > 0 ? sRaw : 1;
    spine.scale.set(s);

    if (dataW > 0 && dataH > 0) {
      const cx = data.x + dataW * 0.5;
      const cy = data.y + dataH * 0.5;
      spine.x = -cx * s;
      spine.y = -cy * s;
    } else if (!holdBounds.isEmpty()) {
      spine.x = -((holdBounds.minX + holdBounds.maxX) * 0.5);
      spine.y = -((holdBounds.minY + holdBounds.maxY) * 0.5);
    }

    this._parkAtHoldPose();
    this._styleAmountText();
    this._layoutAmountAnchor();
  }

  _stopAmountRollup() {
    if (this._amountRollupRaf != null) {
      cancelAnimationFrame(this._amountRollupRaf);
      this._amountRollupRaf = null;
    }
    this._amountRollupSlotWidth = 0;
    if (this._amountMain) this._amountMain.x = 0;
  }

  /**
   * Count up the overlay amount in sync with each popup tier (big → huge → mega → epic).
   * @param {number} gen
   * @param {number} targetWin
   * @param {number} stake
   * @param {number} multiple win ÷ stake
   * @param {boolean} immediate
   */
  _startWinAmountRollup(gen, targetWin, stake, multiple, immediate) {
    this._stopAmountRollup();
    const target = Math.max(0, Number(targetWin) || 0);

    const applyText = (shown) => {
      const t = this._formatCelebrationWinAmount(shown);
      this._amountMain.text = t;
      const slotW = this._amountRollupSlotWidth;
      if (slotW > 0) {
        this._amountMain.x = Math.round((this._amountMain.width - slotW) * 0.5);
      }
    };

    if (immediate || target <= 0) {
      applyText(target);
      return;
    }

    this._amountMain.text = this._formatCelebrationWinAmount(target);
    this._amountRollupSlotWidth = this._amountMain.width;
    this._amountMain.x = 0;

    const timeline = computeCelebrationAmountTimeline(
      this._assets,
      multiple,
      stake,
      target,
      !!this._activeTurbo,
    );
    const startTs = performance.now();
    const tick = () => {
      if (gen !== this._gen) return;
      const elapsed = (performance.now() - startTs) / 1000;
      const shown = timeline
        ? celebrationAmountAtElapsed(timeline, elapsed)
        : Math.min(target, elapsed * (target / Math.max(0.05, Number(multiple) || 1)));
      applyText(shown);
      const done = timeline
        ? elapsed >= timeline.totalSec || shown >= target
        : shown >= target;
      if (done) {
        applyText(target);
        this._amountRollupRaf = null;
        this._amountRollupSlotWidth = 0;
        this._amountMain.x = 0;
        return;
      }
      this._amountRollupRaf = requestAnimationFrame(tick);
    };
    applyText(0);
    this._amountRollupRaf = requestAnimationFrame(tick);
  }

  /** Park the popup at its hold pose before the outro clip (collect / resume). */
  _applyHoldPose() {
    const state = this._spine?.state;
    const skeletonData = this._spine?.skeleton?.data;
    if (!state || !skeletonData) return;

    const clips = tierWinSpineClips(this._activeTier ?? 'big');
    const idleAnim = skeletonData.findAnimation(clips.idle);
    if (idleAnim) {
      const e = state.setAnimation(0, clips.idle, true);
      if (e) e.mixDuration = 0;
      return;
    }

    const chain = tierBuildupChain(this._activeTier ?? 'big');
    const lastName = chain[chain.length - 1];
    const lastAnim = lastName ? skeletonData.findAnimation(lastName) : null;
    if (lastAnim) {
      const e = state.setAnimation(0, lastName, false);
      if (e) {
        e.mixDuration = 0;
        e.trackTime = e.animationEnd;
      }
    }
  }

  /**
   * Show the tier popup at its post-buildup hold pose (no replay) — used when resuming after
   * the player already saw the celebration but has not collected yet.
   * @param {number} multiple win ÷ stake
   * @param {number} totalWinAmount
   */
  showStaticHold(multiple, totalWinAmount) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) return;

    const gen = ++this._gen;
    const state = this._spine.state;
    this._detachEndListener();
    this._stopAmountRollup();
    this._clearBuildupStep();

    state.clearTracks();
    this._spine.skeleton.setToSetupPose();

    this._activeTier = tier;
    this._activeTurbo = false;
    this._buildupComplete = true;
    this._onBuildupComplete = null;
    this._activeWinAmount = totalWinAmount;
    this._activeStake = multiple > 0 ? totalWinAmount / multiple : 0;
    this._activeMultiple = multiple;

    this._applyHoldPose();
    this._applySpineStateNow();
    this._styleAmountText();
    this._syncAmountPosition();
    this.root.visible = true;
    const stake = multiple > 0 ? totalWinAmount / multiple : 0;
    this._startWinAmountRollup(gen, totalWinAmount, stake, multiple, true);
  }

  /**
   * Resume after session interrupt: continue buildup when mid-animation, static hold when buildup finished.
   * @param {number} multiple win ÷ stake
   * @param {number} totalWinAmount
   * @param {number} elapsedSec seconds since celebration started
   * @param {() => void} [onBuildupComplete]
   */
  playContinue(multiple, totalWinAmount, elapsedSec, onBuildupComplete) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) return;

    const seqSec = getCelebrationSequenceDurationSec(this._assets, multiple, false);
    const elapsed = Math.max(0, Number(elapsedSec) || 0);
    if (elapsed >= Math.max(0.05, seqSec * 0.95)) {
      this.showStaticHold(multiple, totalWinAmount);
      onBuildupComplete?.();
      return;
    }

    this.play(multiple, false, totalWinAmount, undefined, onBuildupComplete, true);
  }

  /**
   * @param {number} multiple win ÷ stake
   * @param {boolean} turbo
   * @param {number} totalWinAmount
   * @param {(finished: boolean) => void} [onDone] `true` if the full sequence finished (not skipped).
   * @param {() => void} [onBuildupComplete] Fired when tier build-up finishes and the popup waits for collect.
   * @param {boolean} [skipAnticipation] When true, skip the pre-popup cabinet shake (resume / mid-sequence).
   */
  play(multiple, turbo, totalWinAmount, onDone, onBuildupComplete, skipAnticipation = false) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) {
      onDone?.(false);
      return;
    }

    const gen = ++this._gen;
    const state = this._spine.state;
    sound.stopWinCelebrationSounds({ force: true });
    this._detachEndListener();
    this._stopAmountRollup();
    this._clearBuildupStep();

    state.clearTracks();
    this._spine.skeleton.setToSetupPose();

    this._activeTier = tier;
    this._activeTurbo = !!turbo;
    this._buildupComplete = false;
    this._activeWinAmount = totalWinAmount;
    this._activeStake = multiple > 0 ? totalWinAmount / multiple : 0;
    this._activeMultiple = multiple;
    this._onBuildupComplete = onBuildupComplete ?? null;
    this._inAnticipation = false;

    const idleName = tierWinSpineClips(tier).idle;

    const buildupDone = () => {
      if (gen !== this._gen) return;
      if (this._spine.skeleton.data.findAnimation(idleName)) {
        const idleEntry = state.setAnimation(0, idleName, true);
        if (idleEntry) idleEntry.mixDuration = 0;
      }
      const finalSnd = soundForFinalTier(tier);
      if (finalSnd) sound.ensureFinalWinCelebrationSound(finalSnd);
      this._styleAmountText();
      this._syncAmountPosition();
      this._buildupComplete = true;
      const cb = this._onBuildupComplete;
      this._onBuildupComplete = null;
      cb?.();
    };

    const names = this._resolveBuildupClipNames(tier);
    if (!names.length) {
      this._applyHoldPose();
      this._applySpineStateNow();
      buildupDone();
      onDone?.(false);
      return;
    }

    const beginPopup = () => {
      if (gen !== this._gen) return;
      this._inAnticipation = false;

      this._styleAmountText();
      this._syncAmountPosition();
      this.root.visible = true;
      this._juiceHost?.startCelebrationMode?.();

      const stake = multiple > 0 ? totalWinAmount / multiple : 0;
      this._startWinAmountRollup(gen, totalWinAmount, stake, multiple, false);

      this._playBuildupClipsMinDisplay(gen, 0, names, tier, buildupDone);
    };

    const skipAnticipationForTurbo = turbo || skipAnticipation;
    if (skipAnticipationForTurbo || getCelebrationAnticipationSec() <= 0.01) {
      beginPopup();
      return;
    }

    this._inAnticipation = true;
    const anticipation = this._juiceHost?.playCelebrationAnticipation?.(tier);
    if (anticipation?.then) {
      anticipation.then(() => beginPopup());
    } else {
      this._inAnticipation = false;
      beginPopup();
    }
  }

  /**
   * @param {WinTier} tier
   * @returns {string[]}
   */
  _resolveBuildupClipNames(tier) {
    const chain = tierBuildupChain(tier);
    /** @type {string[]} */
    const names = [];
    for (const name of chain) {
      if (this._spine.skeleton.data.findAnimation(name)) {
        names.push(name);
      } else {
        console.warn(`WinCelebrationOverlay: missing animation "${name}"`);
      }
    }
    return names;
  }

  _clearBuildupStep() {
    if (this._buildupStepTimer != null) {
      clearTimeout(this._buildupStepTimer);
      this._buildupStepTimer = null;
    }
  }

  /**
   * Each intro clip plays once at normal speed; if still under {@link gameConfig.winAnimationMinDisplaySec},
   * the matching idle loop holds until the minimum, then the next clip cuts in instantly.
   * Transition clips (e.g. `big_win_end`) play once with no idle pad.
   * @param {number} gen
   * @param {number} track
   * @param {string[]} clips
   * @param {WinTier} tier
   * @param {() => void} onComplete
   */
  _playBuildupClipsMinDisplay(gen, track, clips, tier, onComplete) {
    const state = this._spine?.state;
    const skeletonData = this._spine?.skeleton?.data;
    if (!state || !skeletonData || !clips.length) {
      onComplete();
      return;
    }

    const { minSec, timeScale: clipTimeScale } = celebrationClipPlayback(!!this._activeTurbo);
    if (state) state.timeScale = clipTimeScale;
    let index = 0;

    const advance = () => {
      this._clearBuildupStep();
      if (gen !== this._gen) return;
      index += 1;
      playAt(index);
    };

    const scheduleAdvanceAfter = (delaySec) => {
      this._clearBuildupStep();
      this._buildupStepTimer = setTimeout(advance, Math.max(0, delaySec) * 1000);
    };

    const holdOnIdle = (idleName, remainingSec) => {
      const remaining = Math.max(0, remainingSec);
      if (remaining <= 0.01) {
        advance();
        return;
      }
      const idleAnim = skeletonData.findAnimation(idleName);
      if (!idleAnim) {
        scheduleAdvanceAfter(remaining);
        return;
      }
      const idleEntry = state.setAnimation(track, idleName, true);
      if (idleEntry) idleEntry.mixDuration = 0;
      this._applySpineStateNow();
      scheduleAdvanceAfter(remaining);
    };

    const playAt = (i) => {
      if (gen !== this._gen) return;
      if (i >= clips.length) {
        onComplete();
        return;
      }

      const name = clips[i];
      const dur = clipDurationSeconds(skeletonData, name);
      const idleName = holdIdleAfterBuildupClip(name);
      const stepStartMs = performance.now();
      let stepSettled = false;

      const entry = state.setAnimation(track, name, false);
      if (entry) {
        entry.mixDuration = 0;
        entry.timeScale = clipTimeScale;
      }
      this._applySpineStateNow();

      const snd = soundForBuildupClipPlayback(name, tier);
      if (snd) {
        sound.playWinCelebrationTierSound(snd, { final: isFinalTierSoundClip(name, tier) });
        const tierStep = CLIP_TIER_START[name];
        if (tierStep) this._juiceHost?.onCelebrationTierStep?.(tierStep);
      }

      const finishStep = () => {
        if (stepSettled || gen !== this._gen) return;
        stepSettled = true;
        const elapsed = (performance.now() - stepStartMs) / 1000;
        if (!idleName) {
          const full = (dur > 0 ? dur : 0) / clipTimeScale;
          const wait = full - elapsed;
          if (wait > 0.01) scheduleAdvanceAfter(wait);
          else advance();
          return;
        }
        holdOnIdle(idleName, Math.max(minSec, dur / clipTimeScale) - elapsed);
      };

      if (!entry || dur <= 0) {
        if (idleName) holdOnIdle(idleName, minSec);
        else scheduleAdvanceAfter(minSec);
        return;
      }

      entry.listener = {
        complete: (e) => {
          if (gen !== this._gen) return;
          if (e.trackIndex !== track) return;
          finishStep();
        },
      };
    };

    playAt(0);
  }

  /**
   * Queue the tier build-up chain on track 0.
   * @param {number} gen
   * @param {WinTier} tier
   * @returns {import('@esotericsoftware/spine-core').TrackEntry | null}
   * @deprecated Use {@link WinCelebrationOverlay#_playBuildupClipsMinDisplay}
   */
  _queueBuildupChain(gen, tier) {
    const state = this._spine?.state;
    if (!state || gen !== this._gen) return null;

    const chain = tierBuildupChain(tier);
    /** @type {string[]} */
    const names = [];
    for (const name of chain) {
      if (this._spine.skeleton.data.findAnimation(name)) {
        names.push(name);
      } else {
        console.warn(`WinCelebrationOverlay: missing animation "${name}"`);
      }
    }
    if (!names.length) return null;

    return this._queueBuildupClips(gen, 0, names, tier);
  }

  /**
   * @param {number} gen
   * @param {number} track
   * @param {string[]} clips
   * @param {WinTier} tier
   * @returns {import('@esotericsoftware/spine-core').TrackEntry | null}
   */
  _queueBuildupClips(gen, track, clips, tier) {
    const state = this._spine?.state;
    if (!state || !clips.length) return null;

    /** @type {import('@esotericsoftware/spine-core').TrackEntry | null} */
    let lastEntry = null;
    for (let i = 0; i < clips.length; i++) {
      const name = clips[i];
      const entry =
        i === 0
          ? state.setAnimation(track, name, false)
          : state.addAnimation(track, name, false, 0);
      if (!entry) continue;
      entry.mixDuration = 0;
      const snd = soundForBuildupClipPlayback(name, tier);
      if (snd && i === 0) {
        sound.playWinCelebrationTierSound(snd, { final: isFinalTierSoundClip(name, tier) });
      } else if (snd && i > 0) {
        entry.listener = {
          start: () => {
            if (gen !== this._gen) return;
            sound.playWinCelebrationTierSound(snd, { final: isFinalTierSoundClip(name, tier) });
          },
        };
      }
      lastEntry = entry;
    }
    return lastEntry;
  }

  /**
   * @param {number} gen
   * @param {import('@esotericsoftware/spine-core').TrackEntry | null} entry
   * @param {() => void} onComplete
   */
  _armComplete(gen, entry, onComplete) {
    if (!entry) {
      onComplete();
      return;
    }
    const prev = entry.listener;
    entry.listener = {
      ...(prev || {}),
      complete: (e) => {
        if (gen !== this._gen) return;
        if (e.trackIndex !== entry.trackIndex) return;
        prev?.complete?.(e);
        onComplete();
      },
    };
  }

  _detachEndListener() {
    this._endListener = null;
  }

  /** True while tier build-up clips are playing (before the final idle / collect hold). */
  isInBuildup() {
    return (
      (!!this._inAnticipation || this.root.visible) &&
      !!this._activeTier &&
      !this._buildupComplete &&
      !this._skipPromise
    );
  }

  /**
   * Skip remaining build-up clips and jump to the final tier hold pose.
   * @returns {boolean} whether build-up was interrupted
   */
  skipToHold() {
    if (this._inAnticipation) {
      this._inAnticipation = false;
      const gen = ++this._gen;
      this._juiceHost?.stopCelebrationMode?.();
      this._clearBuildupStep();
      sound.stopBigWinAlert();
      sound.stopWinCelebrationSounds({ force: true });
      this._detachEndListener();
      this._stopAmountRollup();
      this.root.visible = true;
      this._juiceHost?.startCelebrationMode?.();
      this._applyHoldPose();
      this._applySpineStateNow();
      this._styleAmountText();
      this._syncAmountPosition();
      this._startWinAmountRollup(
        gen,
        this._activeWinAmount,
        this._activeStake,
        this._activeMultiple,
        true,
      );
      this._buildupComplete = true;
      const cb = this._onBuildupComplete;
      this._onBuildupComplete = null;
      cb?.();
      return true;
    }

    if (!this.isInBuildup()) return false;

    const gen = ++this._gen;
    this._clearBuildupStep();
    sound.stopWinCelebrationSounds({ force: true });
    this._detachEndListener();
    this._stopAmountRollup();
    this._applyHoldPose();
    this._applySpineStateNow();
    this._styleAmountText();
    this._syncAmountPosition();
    this._startWinAmountRollup(
      gen,
      this._activeWinAmount,
      this._activeStake,
      this._activeMultiple,
      true,
    );
    this._buildupComplete = true;
    const cb = this._onBuildupComplete;
    this._onBuildupComplete = null;
    cb?.();
    return true;
  }

  /**
   * @param {import('../effects/cabinetJuice.js').CabinetJuice | null} host
   */
  setJuiceHost(host) {
    this._juiceHost = host ?? null;
  }

  /** Hide overlay; tier audio may keep playing when {@link keepFinalTierSound} is set. */
  reset(opts) {
    const keepFinalTierSound = !!(opts && opts.keepFinalTierSound);
    this._juiceHost?.stopCelebrationMode?.();
    this._gen += 1;
    this._inAnticipation = false;
    this._clearBuildupStep();
    sound.stopBigWinAlert();
    sound.stopWinCelebrationSounds({ force: !keepFinalTierSound });
    this._activeTier = null;
    this._activeTurbo = false;
    this._buildupComplete = false;
    this._onBuildupComplete = null;
    this._activeWinAmount = 0;
    this._activeStake = 0;
    this._activeMultiple = 0;
    this._skipPromise = null;
    this._stopAmountRollup();
    this._detachEndListener();
    if (this._amountMain) this._amountMain.text = '';
    if (this._spine?.state) {
      try {
        this._spine.state.clearTracks();
      } catch {
        /* ignore */
      }
    }
    this.root.visible = false;
  }

  /**
   * Collect tap: play the outro clip (`end`), then hide.
   * Idempotent: re-entry while the outro is already playing returns the same in-flight promise.
   * @returns {Promise<void>}
   */
  skip() {
    if (this._skipPromise) return this._skipPromise;

    if (!this.root.visible || !this._spine?.state || !this._activeTier) {
      this.reset();
      return Promise.resolve();
    }

    const outroName = tierWinSpineClips(this._activeTier).outro;
    if (!this._spine.skeleton.data.findAnimation(outroName)) {
      this.reset();
      return Promise.resolve();
    }

    const gen = ++this._gen;
    const state = this._spine.state;
    this._detachEndListener();
    this._stopAmountRollup();

    this._applyHoldPose();

    const entry = state.setAnimation(0, outroName, false);
    if (entry) entry.mixDuration = 0;
    sound.play('popupOut');

    const clipSec = clipDurationSeconds(this._spine.skeleton.data, outroName);
    const timeoutMs = Math.min(8000, Math.max(1200, (clipSec + 0.75) * 1000));

    const promise = new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        if (gen !== this._gen) {
          resolve();
          return;
        }
        this.reset({ keepFinalTierSound: true });
        resolve();
      };
      const timeoutId = setTimeout(() => {
        if (gen !== this._gen) {
          finish();
          return;
        }
        console.warn('WinCelebrationOverlay: skip end clip timed out, forcing reset');
        finish();
      }, timeoutMs);
      this._armComplete(gen, entry, finish);
    }).finally(() => {
      if (this._skipPromise === promise) this._skipPromise = null;
    });
    this._skipPromise = promise;
    return promise;
  }
}
